/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getFlattenMetadata } from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';

export type QnaMakerFiltersType = 'questions' | 'sources' | 'metadata' | 'suggestionsKind';
export type QnaMakerFilterItem = { id: string; filterType: QnaMakerFiltersType; name: string; isChecked: boolean };
export const UnsavedChangesFIlterItem = 'unsavedChanges';
export const QuestionsBeingEditedFilterItem = 'questionsBeingEdited';
export const SmartSuggestionsFilterItem = 'smartSuggestions';
export const ActiveLearningFilterItem = 'activeLearning';

export class QnaMakerFilterStore {
    @observable public questionsFilters: QnaMakerFilterItem[];

    @observable public sourcesFilters: QnaMakerFilterItem[];

    @observable public metadataFilters: QnaMakerFilterItem[];

    @observable public suggestionFilters: QnaMakerFilterItem[];

    constructor(_qnaPairsJson: QnaMakerQnaSwagger[] = [], sourceStores: QnaMakerSourceStore[] = []) {
        this.sourcesFilters = sourceStores.map(s => ({ id: s.id, filterType: 'sources', name: s.name || s.source, isChecked: false }));
        const flattenedMetadata = getFlattenMetadata(_qnaPairsJson);
        this.metadataFilters = [...flattenedMetadata].map(md => ({ id: md, filterType: 'metadata', name: md, isChecked: false }));
        this.questionsFilters = [
            { id: UnsavedChangesFIlterItem, filterType: 'questions', name: 'Unsaved changes', isChecked: false },
            { id: QuestionsBeingEditedFilterItem, filterType: 'questions', name: 'Questions being edited', isChecked: false }
        ];
        this.suggestionFilters = [
            { id: ActiveLearningFilterItem, filterType: 'suggestionsKind', name: 'Active learning', isChecked: false },
            ...(!FeatureToggles.QnaNewEditKBPage
                ? [
                      {
                          id: SmartSuggestionsFilterItem,
                          filterType: 'suggestionsKind',
                          name: 'Smart suggestions',
                          isChecked: false
                      } as QnaMakerFilterItem
                  ]
                : [])
        ];
    }

    @computed
    public get appliedQuestionFilters() {
        return this.questionsFilters.filter(q => q.isChecked);
    }

    @computed
    public get appliedSourcesFilters() {
        return this.sourcesFilters.filter(s => s.isChecked);
    }

    @computed
    public get appliedMetadataFilters() {
        return this.metadataFilters.filter(md => md.isChecked);
    }

    @computed
    public get appliedSuggestionFilters() {
        return this.suggestionFilters.filter(md => md.isChecked);
    }

    @computed
    public get allAppliedFilters() {
        return [
            ...this.appliedMetadataFilters,
            ...this.appliedQuestionFilters,
            ...this.appliedSourcesFilters,
            ...this.appliedSuggestionFilters
        ];
    }

    @action
    public resetFilters(filterType?: QnaMakerFiltersType) {
        switch (filterType) {
            case 'questions':
                this.questionsFilters = this.questionsFilters.map(q => ({ ...q, isChecked: false }));
                break;
            case 'sources':
                this.sourcesFilters = this.sourcesFilters.map(s => ({ ...s, isChecked: false }));
                break;
            case 'metadata':
                this.metadataFilters = this.metadataFilters.map(md => ({ ...md, isChecked: false }));
                break;
            case 'suggestionsKind':
                this.suggestionFilters = this.suggestionFilters.map(sk => ({ ...sk, isChecked: false }));
                break;
            default:
                this.questionsFilters = this.questionsFilters.map(q => ({ ...q, isChecked: false }));
                this.sourcesFilters = this.sourcesFilters.map(s => ({ ...s, isChecked: false }));
                this.metadataFilters = this.metadataFilters.map(md => ({ ...md, isChecked: false }));
                this.suggestionFilters = this.suggestionFilters.map(s => ({ ...s, isChecked: false }));
        }
    }

    @action
    public setFilterItems({
        questionsFilters,
        sourcesFilters,
        metadataFilters,
        suggestionFilters
    }: {
        questionsFilters?: QnaMakerFilterItem[];
        sourcesFilters?: QnaMakerFilterItem[];
        metadataFilters?: QnaMakerFilterItem[];
        suggestionFilters?: QnaMakerFilterItem[];
    }) {
        this.questionsFilters = questionsFilters || this.questionsFilters;
        this.sourcesFilters = sourcesFilters || this.sourcesFilters;
        this.metadataFilters = metadataFilters || this.metadataFilters;
        this.suggestionFilters = suggestionFilters || this.suggestionFilters;
    }

    @action
    public applyQuestionsFilterById(id: string) {
        this.questionsFilters = this.questionsFilters.map(s => (s.id === id ? { ...s, isChecked: true } : s));
    }

    @action
    public applySourceFilterById(id: string) {
        this.sourcesFilters = this.sourcesFilters.map(s => (s.id === id ? { ...s, isChecked: true } : s));
    }

    @action
    public setSourcesFiltersItems(sourceStores: QnaMakerSourceStore[]) {
        this.sourcesFilters = sourceStores.map(s => ({ id: s.id, filterType: 'sources', name: s.name || s.source, isChecked: false }));
    }

    @action
    public setMetadataFiltersItems(_qnaPairsJson: QnaMakerQnaSwagger[]) {
        const flattenedMetadata = getFlattenMetadata(_qnaPairsJson);
        this.metadataFilters = [...flattenedMetadata].map(md => ({ id: md, filterType: 'metadata', name: md, isChecked: false }));
    }
}
